/**
 * ProviderDetailPanel — Integration Hub
 * -----------------------------------------------------------------------------
 * Painel de detalhes de um fornecedor/aplicativo do catálogo de integrações.
 *
 * Reutilizado em dois contextos:
 *   - variant="page"   -> página fixa /tenant/data/connections/:providerSlug
 *   - variant="dialog" -> navegação interna do modal "Aplicativos e integrações"
 *
 * Estrutura (abas):
 *   - Visão geral   : descrição, resumo dos recursos e detalhes técnicos
 *   - Conexões      : N conexões por provider, com conexão PADRÃO
 *   - Recursos      : liga/desliga cada operação na conexão escolhida
 *   - Documentação  : conteúdo de ajuda (markdown) vindo do banco
 *
 * O formulário de conexão é uma VISTA deste painel (não um modal aninhado),
 * o que dá largura total ao conteúdo e escala para providers com dezenas de
 * endpoints.
 *
 * @version 1.6.0
 * RELEASE: 2026-08-29 v1.6.0 - Botão "Consultar" por recurso (modal dinâmico) e novas
 *          abas "Histórico" (consultas + PDF) e "Logs" (chamadas técnicas), ambas
 *          restritas a administradores do tenant.
 * RELEASE: 2026-08-24 v1.5.0 - Nova aba "Envios" para aplicativos de saída
 *          (transporte de banco de dados): configuração do envio automático de
 *          contratos assinados e histórico de entregas com reenvio.

 * RELEASE: 2026-08-24 v1.4.0 - Status das conexões e situação do conector passam a
 *          usar bolinha + texto (ConnectionStatusDot), sem badges coloridos.
 * RELEASE: 2026-08-23 v1.3.0 - Refatoração visual "Editorial": cabeçalho do provider
 *          com bloco de marca e badge de categoria, abas em formato underline
 *          (sem pílulas) e visão geral em duas colunas (conteúdo + painel lateral
 *          de Recursos/Detalhes). Somente apresentação.
 * RELEASE: 2026-08-23 v1.2.0 - Abas (Visão geral/Conexões/Recursos/Documentação)
 *          e formulário de conexão inline (fim do modal dentro de modal).
 * RELEASE: 2026-08-23 v1.1.0 - Recursos por conexão: switches para ligar/desligar
 *          cada operação e contador de recursos ativos por conexão.
 * RELEASE: 2026-08-23 v1.0.0 - Extraído de ProviderPage para permitir navegação
 *          do conector dentro do próprio modal de aplicativos.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ExternalLink,
  Loader2,
  Pencil,
  Plug,
  Plus,
  Star,
  Trash2,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { categoryLabel } from '@/components/data/IntegrationCatalogDialog';
import { ConnectionFormPanel } from '@/components/data/ConnectionFormPanel';
import { ProviderOperationList } from '@/components/data/ProviderOperationList';
import { ProviderDocsPanel, MarkdownBlock } from '@/components/data/ProviderDocsPanel';
import { ConnectionStatusDot } from '@/components/data/ConnectionStatusDot';
import { OutboundDeliveriesPanel } from '@/components/data/OutboundDeliveriesPanel';
import { OperationRunDialog } from '@/components/data/OperationRunDialog';
import { ConsultationHistoryPanel } from '@/components/data/ConsultationHistoryPanel';
import { IntegrationLogsPanel } from '@/components/data/IntegrationLogsPanel';
import {
  useIntegrationConnections,
  type IntegrationConnection,
  type IntegrationOperation,
} from '@/hooks/integrations/useIntegrationConnections';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';


/** Rótulos amigáveis dos tipos de autenticação */
const AUTH_LABELS: Record<string, string> = {
  api_key: 'Chave de API',
  bearer_token: 'Token Bearer',
  basic_auth: 'Usuário e senha',
  custom: 'Personalizada',
};

interface ProviderDetailPanelProps {
  /** Slug do provider exibido */
  providerSlug: string;
  /** Callback do breadcrumb (voltar para catálogo/lista) */
  onBack?: () => void;
  /** Contexto de renderização (ajusta apenas espaçamentos e rótulo do breadcrumb) */
  variant?: 'page' | 'dialog';
  /** Abre o formulário direto em uma conexão específica (deep-link ?edit=) */
  initialEditConnectionId?: string | null;
  /** Disparado após consumir o initialEditConnectionId (limpar querystring) */
  onInitialEditConsumed?: () => void;
}

export function ProviderDetailPanel({
  providerSlug,
  onBack,
  variant = 'page',
  initialEditConnectionId = null,
  onInitialEditConsumed,
}: ProviderDetailPanelProps) {
  /** Vista atual: detalhes do conector ou formulário de conexão */
  const [view, setView] = useState<'detail' | 'form'>('detail');
  const [editing, setEditing] = useState<IntegrationConnection | null>(null);
  const [deleting, setDeleting] = useState<IntegrationConnection | null>(null);
  const [tab, setTab] = useState('overview');
  /** Conexão cujos recursos estão sendo configurados na aba Recursos */
  const [resourceConnectionId, setResourceConnectionId] = useState<string | null>(null);

  /** Estado do fluxo "Consultar": modal de parâmetros (execução é assíncrona) */
  const [runOperation, setRunOperation] = useState<IntegrationOperation | null>(null);
  const [runOpen, setRunOpen] = useState(false);

  /** Somente administradores enxergam histórico e logs (o banco também valida) */
  const { isAdmin } = usePermissionsContext();


  const {
    providers,
    isLoadingProviders,
    connections,
    isLoading,
    saveConnection,
    deleteConnection,
    testConnection,
    setDefaultConnection,
    setConnectionOperation,
  } = useIntegrationConnections({ providerSlug });

  const provider = useMemo(
    () => providers.find((p) => p.slug === providerSlug) ?? null,
    [providers, providerSlug],
  );

  /** Conexão usada na aba Recursos (escolhida, padrão ou a primeira) */
  const resourceConnection = useMemo(
    () =>
      connections.find((c) => c.id === resourceConnectionId) ??
      connections.find((c) => c.is_default) ??
      connections[0] ??
      null,
    [connections, resourceConnectionId],
  );

  /** Abertura direta em modo edição (deep-link) */
  useEffect(() => {
    if (!initialEditConnectionId || connections.length === 0) return;
    const target = connections.find((c) => c.id === initialEditConnectionId);
    if (!target) return;
    setEditing(target);
    setView('form');
    onInitialEditConsumed?.();
  }, [initialEditConnectionId, connections, onInitialEditConsumed]);

  /** Ao trocar de conector, volta para os detalhes */
  useEffect(() => {
    setView('detail');
    setEditing(null);
    setTab('overview');
    setResourceConnectionId(null);
  }, [providerSlug]);

  // Conectado = alguma conexão ativa com status 'active' (ou legado 'connected')
  const isConnected = connections.some(
    (c) => (c.status === 'active' || c.status === 'connected') && c.is_active,
  );

  /** Aplicativo de saída: envia dados (banco de destino), não consulta */
  const isOutbound = provider?.transport === 'mysql';

  /** Abre o formulário em modo criação */
  const handleCreate = () => {
    setEditing(null);
    setView('form');
  };

  if (isLoadingProviders) {
    return (
      <div className="space-y-3 p-6">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20 text-center">
        <Plug className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm font-medium">Aplicativo não encontrado</p>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            Voltar
          </Button>
        )}
      </div>
    );
  }

  // ───────────── VISTA: formulário de conexão (sem modal aninhado) ─────────────
  if (view === 'form') {
    return (
      <div className={cn('h-full', variant === 'page' && 'mx-auto max-w-5xl')}>
        <ConnectionFormPanel
          provider={provider}
          connection={editing}
          isSaving={saveConnection.isPending}
          onSubmit={(input) => saveConnection.mutateAsync(input)}
          onCancel={() => {
            setView('detail');
            setEditing(null);
          }}
          onSaved={() => {
            setView('detail');
            setEditing(null);
            setTab('connections');
          }}
        />
      </div>
    );
  }

  // ───────────── VISTA: detalhes do conector ─────────────
  return (
    <div
      className={cn(
        'w-full space-y-5',
        variant === 'page'
          ? 'mx-auto max-w-5xl p-4 sm:p-6'
          : 'h-full overflow-y-auto p-4 sm:p-6',
      )}
    >
      {/* Breadcrumb */}
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {variant === 'dialog' ? 'Conectores' : 'Conexões'}
        </button>
      )}

      {/* Cabeçalho do app (bloco de marca + badge de categoria + ação principal) */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border bg-muted/60">
            <Plug className="h-6 w-6 text-foreground" />
          </div>
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-xl font-extrabold tracking-tight text-foreground">
                {provider.name}
              </h1>
              <Badge
                variant="outline"
                className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
              >
                {categoryLabel(provider.category)}
              </Badge>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <ConnectionStatusDot
                label={isConnected ? 'Conectado' : 'Não conectado'}
                dotClassName={isConnected ? 'bg-emerald-500' : 'bg-muted-foreground'}
              />
              {provider.description ? <span>· {provider.description}</span> : null}
            </p>
          </div>
        </div>

        <Button onClick={handleCreate} className="gap-1.5">
          <Plus className="h-4 w-4" />
          Nova conexão
        </Button>
      </div>

      {/* Abas — formato underline editorial (sem pílulas) */}
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="h-auto w-full justify-start gap-8 rounded-none border-b bg-transparent p-0">
          {[
            { value: 'overview', label: 'Visão geral', badge: null as string | null },
            {
              value: 'connections',
              label: 'Conexões',
              badge: connections.length > 0 ? String(connections.length) : null,
            },
            // Aplicativos de saída (banco) não têm recursos de leitura: mostram "Envios"
            ...(isOutbound
              ? [{ value: 'outbound', label: 'Envios', badge: null as string | null }]
              : [
                  {
                    value: 'resources',
                    label: 'Recursos',
                    badge:
                      provider.operations.length > 0 ? String(provider.operations.length) : null,
                  },
                ]),
            // Histórico e Logs: só para administradores e aplicativos de consulta
            ...(!isOutbound && isAdmin
              ? [
                  { value: 'history', label: 'Histórico de consultas', badge: null as string | null },
                  { value: 'logs', label: 'Logs', badge: null as string | null },
                ]
              : []),
            { value: 'docs', label: 'Documentação', badge: null },

          ].map((item) => (
            <TabsTrigger
              key={item.value}
              value={item.value}
              className={cn(
                'relative rounded-none border-b-2 border-transparent bg-transparent px-0 pb-3 pt-0 text-sm font-medium text-muted-foreground shadow-none',
                'data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:text-foreground data-[state=active]:shadow-none',
              )}
            >
              {item.label}
              {item.badge && (
                <span className="ml-1.5 text-[10px] font-bold text-muted-foreground">
                  {item.badge}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>


        {/* ─────────── Visão geral (duas colunas: conteúdo + painel lateral) ─────────── */}
        <TabsContent value="overview" className="pt-6">
          <div className="grid gap-8 lg:grid-cols-3">
            {/* Coluna principal */}
            <div className="space-y-6 lg:col-span-2">
              <section className="space-y-2">
                <p className="text-sm leading-relaxed text-foreground/90">
                  {provider.description ||
                    `Conecte o ${provider.name} ao Uaizy para consultar e importar dados.`}
                </p>
                {provider.overview_md && <MarkdownBlock content={provider.overview_md} />}
              </section>

              {provider.operations.length > 0 && (
                <section className="space-y-3">
                  <h2 className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                    O que dá para fazer
                  </h2>
                  <ul className="space-y-2">
                    {provider.operations.map((operation) => (
                      <li
                        key={operation.code}
                        className="flex gap-2 text-sm leading-relaxed text-foreground"
                        title={operation.description ?? undefined}
                      >
                        <span className="text-muted-foreground">—</span>
                        <span>{operation.name}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>

            {/* Painel lateral: resumo e detalhes técnicos */}
            <aside className="space-y-4">
              <div className="rounded-md border p-4">
                <h2 className="mb-3 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  Resumo
                </h2>
                <dl className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Conexões</dt>
                    <dd className="font-semibold text-foreground">{connections.length}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Recursos</dt>
                    <dd className="font-semibold text-foreground">
                      {provider.operations.length}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Situação</dt>
                    <dd className="font-semibold text-foreground">
                      <ConnectionStatusDot
                        label={isConnected ? 'Conectado' : 'Não conectado'}
                        dotClassName={isConnected ? 'bg-emerald-500' : 'bg-muted-foreground'}
                        className="text-sm font-semibold"
                      />
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="rounded-md border p-4">
                <h2 className="mb-3 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  Detalhes
                </h2>
                <dl className="space-y-2 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-muted-foreground">Autenticação</dt>
                    <dd className="text-right font-semibold text-foreground">
                      {AUTH_LABELS[provider.auth_type] ?? provider.auth_type}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-muted-foreground">Categoria</dt>
                    <dd className="text-right font-semibold text-foreground">
                      {categoryLabel(provider.category)}
                    </dd>
                  </div>
                  {provider.docs_url && (
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-muted-foreground">Suporte</dt>
                      <dd>
                        <a
                          href={provider.docs_url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1 font-semibold text-foreground underline underline-offset-2"
                        >
                          Documentação
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </aside>
          </div>
        </TabsContent>


        {/* ─────────── Conexões ─────────── */}
        <TabsContent value="connections" className="space-y-3 pt-6">
          <p className="text-xs text-muted-foreground">
            Você pode manter várias conexões deste app. A conexão padrão é usada quando nenhuma for
            escolhida explicitamente.
          </p>

          {isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : connections.length === 0 ? (
            <div className="rounded-lg border py-12 text-center text-sm text-muted-foreground">
              Nenhuma conexão configurada para este aplicativo.
            </div>
          ) : (
            <div className="space-y-2">
              {connections.map((connection) => {
                const isTesting =
                  testConnection.isPending && testConnection.variables === connection.id;

                return (
                  <div
                    key={connection.id}
                    className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-sm font-medium">{connection.name}</span>
                        <ConnectionStatusDot status={connection.status} />
                        {connection.is_default && (
                          <Badge variant="outline" className="gap-1">
                            <Star className="h-3 w-3" />
                            Padrão
                          </Badge>
                        )}
                        {!connection.is_active && <Badge variant="outline">Inativa</Badge>}
                      </div>
                      <p className="truncate text-xs text-muted-foreground">
                        {connection.base_url || 'URL base não informada'}
                        {' · '}
                        {connection.enabled_operations_count}/{connection.operations_count} recursos
                        ativos
                      </p>
                      {connection.status === 'error' && connection.last_error && (
                        <p className="truncate text-xs text-destructive">{connection.last_error}</p>
                      )}
                    </div>

                    <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                      {!connection.is_default && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5"
                          disabled={setDefaultConnection.isPending}
                          onClick={() => setDefaultConnection.mutate(connection.id)}
                        >
                          <Star className="h-3.5 w-3.5" />
                          Definir como padrão
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        disabled={isTesting}
                        onClick={() => testConnection.mutate(connection.id)}
                      >
                        {isTesting ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Zap className="h-3.5 w-3.5" />
                        )}
                        Testar
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setEditing(connection);
                          setView('form');
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive"
                        onClick={() => setDeleting(connection)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ─────────── Recursos ─────────── */}
        <TabsContent value="resources" className="space-y-3 pt-6">
          {!resourceConnection ? (
            <p className="text-sm text-muted-foreground">
              Adicione uma conexão para escolher quais recursos deste aplicativo ficam ativos.
            </p>
          ) : (
            <>
              {connections.length > 1 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-muted-foreground">Configurando a conexão:</span>
                  <Select
                    value={resourceConnection.id}
                    onValueChange={(value) => setResourceConnectionId(value)}
                  >
                    <SelectTrigger className="h-8 w-[240px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {connections.map((connection) => (
                        <SelectItem key={connection.id} value={connection.id}>
                          {connection.name}
                          {connection.is_default ? ' (padrão)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <ProviderOperationList
                operations={provider.operations}
                enabledCodes={(resourceConnection.enabled_operations ?? [])
                  .filter((op) => op.is_enabled)
                  .map((op) => op.code)}
                mode="toggle"
                showBulkActions={false}
                disabled={setConnectionOperation.isPending}
                maxHeightClassName="max-h-none"
                onToggle={(code, enabled) =>
                  setConnectionOperation.mutate({
                    connectionId: resourceConnection.id,
                    operationCode: code,
                    enabled,
                  })
                }
                onRun={(operation) => {
                  setRunOperation(operation);
                  setRunOpen(true);
                }}
              />
            </>
          )}
        </TabsContent>

        {/* ─────────── Envios (aplicativos de saída) ─────────── */}
        {isOutbound && (
          <TabsContent value="outbound" className="space-y-3 pt-6">
            <OutboundDeliveriesPanel connections={connections} />
          </TabsContent>
        )}

        {/* ─────────── Histórico de consultas (admin) ─────────── */}
        {!isOutbound && isAdmin && (
          <TabsContent value="history" className="space-y-3 pt-6">
            <ConsultationHistoryPanel
              providerSlug={provider.slug}
              operations={provider.operations}
              onRerun={(operationCode) => {
                const operation = provider.operations.find((op) => op.code === operationCode);
                if (!operation) return;
                setRunOperation(operation);
                setRunOpen(true);
              }}
            />
          </TabsContent>
        )}

        {/* ─────────── Logs técnicos (admin) ─────────── */}
        {!isOutbound && isAdmin && (
          <TabsContent value="logs" className="space-y-3 pt-6">
            <IntegrationLogsPanel providerSlug={provider.slug} />
          </TabsContent>
        )}




        {/* ─────────── Documentação ─────────── */}
        <TabsContent value="docs" className="pt-6">
          <ProviderDocsPanel
            providerName={provider.name}
            overviewMd={provider.overview_md}
            setupGuideMd={provider.setup_guide_md}
            docsUrl={provider.docs_url}
            supportUrl={provider.support_url}
          />
        </TabsContent>
      </Tabs>

      {/* Modal de execução do recurso (parâmetros dinâmicos) */}
      <OperationRunDialog
        open={runOpen}
        onOpenChange={setRunOpen}
        operation={runOperation}
        connections={connections}
        defaultConnectionId={resourceConnection?.id ?? null}
        onQueued={({ operation }) => {
          // A consulta roda em segundo plano: acompanha pelo histórico
          toast.info(
            `Consulta "${operation.name}" enviada para processamento. O resultado aparece no histórico.`,
          );
          if (isAdmin) setTab('history');
        }}
      />

      {/* Confirmação de exclusão */}

      <AlertDialog open={Boolean(deleting)} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir conexão</AlertDialogTitle>
            <AlertDialogDescription>
              A conexão "{deleting?.name}" e suas credenciais serão removidas. Importações e
              consultas que dependem dela deixarão de funcionar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleting) deleteConnection.mutate(deleting.id);
                setDeleting(null);
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
